import React from 'react';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Database,
  Shield,
  Trash2,
  FileBarChart,
  LogOut,
  X,
  Layers,
  KeyRound,
} from 'lucide-react';
import { EdgeFinancesLogo } from '../components/common/EdgeFinancesLogo';
import { PRODUCT_NAME } from '../lib/tenant';
import type { SuperAdminSection } from './types';

const NAV: { id: SuperAdminSection; label: string; icon: React.ElementType; group: string }[] = [
  { id: 'overview', label: 'Command center', icon: LayoutDashboard, group: 'Platform' },
  { id: 'tenants', label: 'Tenant registry', icon: Building2, group: 'Platform' },
  { id: 'billing', label: 'Billing overview', icon: CreditCard, group: 'Revenue' },
  { id: 'plans', label: 'Plans & pricing', icon: Layers, group: 'Revenue' },
  { id: 'trials', label: 'Free trials', icon: KeyRound, group: 'Revenue' },
  { id: 'usage', label: 'Usage & storage', icon: Database, group: 'Operations' },
  { id: 'access', label: 'Access control', icon: Shield, group: 'Compliance' },
  { id: 'deletions', label: 'Deletion requests', icon: Trash2, group: 'Compliance' },
  { id: 'reports', label: 'Reports & exports', icon: FileBarChart, group: 'Intelligence' },
];

interface Props {
  active: SuperAdminSection;
  onNavigate: (id: SuperAdminSection) => void;
  onLogout: () => void;
  onCloseMobile?: () => void;
}

export function SuperAdminSidebar({ active, onNavigate, onLogout, onCloseMobile }: Props) {
  const groups = [...new Set(NAV.map((n) => n.group))];

  return (
    <aside className="flex h-full w-full flex-col border-r border-zinc-800 bg-zinc-950 text-zinc-100">
      <div className="flex items-center justify-between border-b border-zinc-800/80 px-4 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <EdgeFinancesLogo variant="onDark" size="sm" showWordmark={false} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{PRODUCT_NAME}</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-400/90">Super Admin</p>
          </div>
        </div>
        {onCloseMobile ? (
          <button type="button" onClick={onCloseMobile} className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-900 lg:hidden">
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {groups.map((group) => (
          <div key={group} className="mb-4">
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{group}</p>
            <div className="space-y-0.5">
              {NAV.filter((n) => n.group === group).map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      onNavigate(item.id);
                      onCloseMobile?.();
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-md py-2 pl-2.5 pr-2 text-left text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-l-2 border-l-emerald-500 bg-zinc-800 text-white'
                        : 'border-l-2 border-l-transparent text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100'
                    }`}
                  >
                    <Icon className={`h-[17px] w-[17px] shrink-0 ${isActive ? 'text-emerald-400' : 'text-zinc-500'}`} />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-zinc-800 p-3">
        <button
          type="button"
          onClick={onLogout}
          className="flex w-full items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-200 hover:bg-zinc-800"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
